import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, unref as _unref, createVNode as _createVNode, createCommentVNode as _createCommentVNode, createBlock as _createBlock, withModifiers as _withModifiers, Transition as _Transition, withCtx as _withCtx, Teleport as _Teleport } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "read-receipts-popover__list"
}
const _hoisted_2 = { class: "read-receipts-popover__recipient" }
const _hoisted_3 = { class: "read-receipts-popover__event-info" }
const _hoisted_4 = { class: "read-receipts-popover__event-type" }
const _hoisted_5 = { class: "read-receipts-popover__timestamp" }
const _hoisted_6 = {
  key: 0,
  class: "read-receipts-popover__clicked-url"
}
const _hoisted_7 = {
  key: 1,
  class: "read-receipts-popover__empty"
}
const _hoisted_8 = { key: 1 }

import { Teleport, nextTick, onUnmounted, ref, watch } from "vue";
import { Icon } from "@iconify/vue";
import type { ContactEventHistory } from "@/dtos/Unipile.dto";
import LoadingBars from "@/components/ui/LoadingBars.vue";

interface ReadReceipt {
  id?: string;
  readAt?: string;
  eventTimestamp?: string;
  readCount?: number;
  formattedDate?: string;
  [key: string]: any;
}

interface Props {
  show: boolean;
  readReceipts?: ReadReceipt[];
  readCount?: number;
  triggerElement?: HTMLElement | null;
  isLoading?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ReadReceiptsPopover',
  props: {
    show: { type: Boolean },
    readReceipts: { default: (): ReadReceipt[] => [] },
    readCount: { default: 0 },
    triggerElement: { default: null },
    isLoading: { type: Boolean, default: false }
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const popoverRef = ref<HTMLElement | null>(null);
const popoverStyle = ref<Record<string, string>>({});
const arrowStyle = ref<Record<string, string>>({});

const formatTimestamp = (timestamp: string): string => {
  if (!timestamp) return "";
  // Format: "Tue, Dec 23, 2025 at 01:41 PM"
  const date = new Date(timestamp);
  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const weekday = weekdays[date.getDay()];
  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const minutesStr = minutes < 10 ? `0${minutes}` : minutes;
  return `${weekday}, ${month} ${day}, ${year} at ${hours}:${minutesStr} ${ampm}`;
};

const getReadText = (readCount: number): string => {
  return readCount === 1 ? "Read 1 time" : `Read ${readCount} times`;
};

const updatePosition = () => {
  if (!props.triggerElement || !popoverRef.value) return;

  const triggerRect = props.triggerElement.getBoundingClientRect();
  const popoverRect = popoverRef.value.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let top = triggerRect.bottom + 8;
  let left = triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2;
  let arrowTop = -8;
  let arrowLeft = "50%";
  let transform = "translateX(-50%)";

  if (left < 8) {
    left = 8;
    arrowLeft = `${triggerRect.left + triggerRect.width / 2 - 8}px`;
    transform = "none";
  } else if (left + popoverRect.width > viewportWidth - 8) {
    left = viewportWidth - popoverRect.width - 8;
    arrowLeft = `${triggerRect.right - (viewportWidth - popoverRect.width - 8)}px`;
    transform = "none";
  }

  if (top + popoverRect.height > viewportHeight - 8) {
    top = triggerRect.top - popoverRect.height - 8;
    arrowTop = popoverRect.height;
    arrowStyle.value = {
      top: `${arrowTop}px`,
      left: arrowLeft,
      transform,
      borderTop: "8px solid #ffffff",
      borderBottom: "none",
    };
  } else {
    arrowStyle.value = {
      top: `${arrowTop}px`,
      left: arrowLeft,
      transform,
      borderBottom: "8px solid #ffffff",
      borderTop: "none",
    };
  }

  popoverStyle.value = {
    top: `${top}px`,
    left: `${left}px`,
  };
};

watch(
  () => props.show,
  async (isShowing) => {
    if (isShowing) {
      await nextTick();
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
    } else {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  window.removeEventListener("scroll", updatePosition, true);
  window.removeEventListener("resize", updatePosition);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createVNode(_Transition, { name: "popover-fade" }, {
      default: _withCtx(() => [
        (__props.show)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              ref_key: "popoverRef",
              ref: popoverRef,
              class: "read-receipts-popover",
              style: _normalizeStyle(popoverStyle.value),
              onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"]))
            }, [
              _createElementVNode("div", {
                class: "read-receipts-popover__arrow",
                style: _normalizeStyle(arrowStyle.value)
              }, null, 4),
              _cache[3] || (_cache[3] = _createElementVNode("h4", { class: "read-receipts-popover__title" }, "Read Receipts", -1)),
              (__props.readReceipts && __props.readReceipts.length > 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.readReceipts, (receipt, index) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: receipt.id || index,
                        class: "read-receipts-popover__item"
                      }, [
                        _createElementVNode("div", _hoisted_2, _toDisplayString(receipt.recipientEmail || "Unknown recipient"), 1),
                        _createElementVNode("div", _hoisted_3, [
                          _createElementVNode("span", _hoisted_4, _toDisplayString(receipt.eventType?.toUpperCase() === "EMAIL_CLICKED" ? "Link Clicked" : "Opened"), 1),
                          _cache[2] || (_cache[2] = _createElementVNode("span", { class: "read-receipts-popover__separator" }, " • ", -1)),
                          _createElementVNode("span", _hoisted_5, _toDisplayString(receipt.formattedDate ||
                (receipt.readAt || receipt.eventTimestamp
                  ? formatTimestamp(receipt.readAt || receipt.eventTimestamp!)
                  : "")), 1)
                        ]),
                        (receipt.eventType === 'EMAIL_CLICKED' && receipt.url)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                              _createVNode(_unref(Icon), { icon: "mdi:link" }),
                              _createElementVNode("span", null, _toDisplayString(receipt.url), 1)
                            ]))
                          : _createCommentVNode("", true)
                      ]))
                    }), 128))
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                    (__props.isLoading)
                      ? (_openBlock(), _createBlock(LoadingBars, {
                          key: 0,
                          size: "sm",
                          "show-text": false
                        }))
                      : (_openBlock(), _createElementBlock("p", _hoisted_8, "No read receipts yet"))
                  ]))
            ], 4))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    (__props.show)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "read-receipts-popover__backdrop",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('close')))
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})